import React from 'react';
import { State } from 'store/state';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import { IInsight } from 'utils/entities';
import {
  createAreaHighlightsAboutToComeListSelector,
  createAreaHighlightsGoodToKnowListSelector,
  insightsLoadingSelector,
} from 'store/state/domainData/selectors';
import {
  setActiveAboutToComeCarouselIdx,
  setActiveGoodToKnowCarouselIdx,
  setActiveAreaHighlights,
} from 'store/state/insightsContext/actions';
import { H2, H3, Text } from 'ds/components/typography';

import { AreaHighlightCarousel } from './AreaHighlightCarousel';
import {
  AreaHighlightCarouselHeader,
  AreaHighlightCarouselsWrapper,
  CarouselWrapper,
  AreaHighlightHeader,
  AreaHighlightHeaderTextWrapper,
  EmptyInsightsSection,
  SectionHeader,
} from './styled';
import { AreaHighlightItem } from './types';
import {
  activeAreaHighlightsAboutToComeCarouselIdxSelector,
  activeAreaHighlightsGoodToKnowCarouselIdxSelector,
} from 'store/state/insightsContext/selectors';
import ExploreAreaIcon from 'assets/svg/explore-area.svg';
import { ShowAt } from 'consts/breakpoints';
import { AreaHighlightCardSkeleton } from 'components/area-highlights/AreaHighlightCardSkeleton';
import { WithContentSlotSidePaddingContainer, WithContentSlotTopPadding } from 'ds/components/Layout';
import { Route, useRoute } from 'config/routes';
import { Section as SectionEnum } from 'components/navigation/subheader/SectionsNavigation';
import { ScrollSpyTarget } from '../scroll-spy';


interface AreaHighlightsProps {
  areaHighlightsGoodToKnowList: AreaHighlightItem[];
  areaHighlightsAboutToComeList: AreaHighlightItem[];
  setActiveInsightId: (id: string) => void;
  activeAreaHighlightsAboutToComeCarouselIdx: number;
  activeAreaHighlightsGoodToKnowCarouselIdx: number;
  setActiveAboutToComeId: (index: number) => void;
  setActiveGoodToKnowId: (index: number) => void;
  areaHighlightsListLoading: boolean;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  hidePadding?: boolean;
  oldStyle?: boolean;
  isWhiteBg?: boolean;
  withoutWrapper?: boolean;
  bigCarouselHeaders?: boolean;
  scrollSpyTargetId: SectionEnum;
}

export const areaHighlightsMap = (insight: IInsight) => ({
  isGoodTradeoff: insight.tradeoff.goodTradeoff,
  insightType: insight.category,
  areaMainInfo: insight.tradeoff.value,
  id: insight.id,
});

const AreaHighlights: React.FC<AreaHighlightsProps> = ({
  areaHighlightsGoodToKnowList,
  areaHighlightsAboutToComeList,
  activeAreaHighlightsAboutToComeCarouselIdx,
  activeAreaHighlightsGoodToKnowCarouselIdx,
  setActiveInsightId,
  areaHighlightsListLoading,
  setActiveAboutToComeId,
  setActiveGoodToKnowId,
  title,
  subTitle,
  hidePadding,
  oldStyle,
  isWhiteBg,
  withoutWrapper,
  bigCarouselHeaders,
  scrollSpyTargetId,
}) => {
  const { t } = useLocale();
  const { route } = useRoute();

  const content = (
    <AreaHighlightCarouselsWrapper data-auto="area-highlight" hidePadding={hidePadding}>
      <ShowAt at={1}>
        {(isMobile) => areaHighlightsListLoading ? <AreaHighlightCardSkeleton itemsNumber={isMobile ? 1 : 2}/> : (
          <>
            {title ? (
              <WithContentSlotSidePaddingContainer>
                <AreaHighlightHeader>
                  <ExploreAreaIcon/>
                  <AreaHighlightHeaderTextWrapper>
                    <H2 weight="bold">{title}</H2>
                    <Text>{subTitle}</Text>
                  </AreaHighlightHeaderTextWrapper>
                </AreaHighlightHeader>
              </WithContentSlotSidePaddingContainer>
            ) : null}
            {areaHighlightsGoodToKnowList.length ? (
              <CarouselWrapper data-auto="good-to-know-carousel" hidePadding={hidePadding}>
                {bigCarouselHeaders ? (
                  <SectionHeader>
                    <WithContentSlotSidePaddingContainer>
                      <H3 weight="bold">{t('unitPage.insights.goodToKnow')}</H3>
                    </WithContentSlotSidePaddingContainer>
                  </SectionHeader>
                ) : (
                  <WithContentSlotSidePaddingContainer>
                    <AreaHighlightCarouselHeader weight="medium">
                      {t('unitPage.insights.goodToKnow')}
                    </AreaHighlightCarouselHeader>
                  </WithContentSlotSidePaddingContainer>
                )}
                <AreaHighlightCarousel
                  oldStyle={oldStyle}
                  source="good_to_know"
                  setActiveInsightId={setActiveInsightId}
                  areaHighlightCardList={areaHighlightsGoodToKnowList}
                  onSlideIndexChange={setActiveGoodToKnowId}
                  activeCarouselSlide={activeAreaHighlightsGoodToKnowCarouselIdx}
                  isWhiteBg={isWhiteBg}
                />
              </CarouselWrapper>
            ) : null}
            {areaHighlightsAboutToComeList.length ? (
              <CarouselWrapper data-auto="about-to-come-carousel">
                {bigCarouselHeaders ? (
                  <SectionHeader>
                    <WithContentSlotSidePaddingContainer>
                      <H3 weight="bold">{t('unitPage.insights.aboutToCome')}</H3>
                    </WithContentSlotSidePaddingContainer>
                  </SectionHeader>
                ) : (
                  <WithContentSlotSidePaddingContainer>
                    <AreaHighlightCarouselHeader weight="medium">
                      {t('unitPage.insights.aboutToCome')}
                    </AreaHighlightCarouselHeader>
                  </WithContentSlotSidePaddingContainer>
                )}
                <AreaHighlightCarousel
                  oldStyle={oldStyle}
                  source="about_to_come"
                  activeCarouselSlide={activeAreaHighlightsAboutToComeCarouselIdx}
                  onSlideIndexChange={setActiveAboutToComeId}
                  setActiveInsightId={setActiveInsightId}
                  areaHighlightCardList={areaHighlightsAboutToComeList}
                  isWhiteBg={isWhiteBg}
                />
              </CarouselWrapper>
            ) : null}
            {!areaHighlightsGoodToKnowList.length && !areaHighlightsAboutToComeList.length && route.name === Route.LocalPage ? (
              <EmptyInsightsSection>
                <Text>{t('localPage.emptyInsightsSection')}</Text>
              </EmptyInsightsSection>
            ) : null}
          </>
        )}
      </ShowAt>
    </AreaHighlightCarouselsWrapper>
  );

  return (
    <ScrollSpyTarget targetId={scrollSpyTargetId}>{withoutWrapper ? content : (
      <WithContentSlotTopPadding>{content}</WithContentSlotTopPadding>
    )}</ScrollSpyTarget>
  );
};

const areaHighlightsListGoodToKnowSelector = createAreaHighlightsGoodToKnowListSelector(areaHighlightsMap);
const areaHighlightsListAboutToComeSelector = createAreaHighlightsAboutToComeListSelector(areaHighlightsMap);

const mapStateToProps = (state: State) => ({
  areaHighlightsGoodToKnowList: areaHighlightsListGoodToKnowSelector(state),
  areaHighlightsAboutToComeList: areaHighlightsListAboutToComeSelector(state),
  areaHighlightsListLoading: insightsLoadingSelector(state),
  activeAreaHighlightsAboutToComeCarouselIdx: activeAreaHighlightsAboutToComeCarouselIdxSelector(state),
  activeAreaHighlightsGoodToKnowCarouselIdx: activeAreaHighlightsGoodToKnowCarouselIdxSelector(state),
});

const mapDispatchToProps = {
  setActiveInsightId: setActiveAreaHighlights,
  setActiveAboutToComeId: setActiveAboutToComeCarouselIdx,
  setActiveGoodToKnowId: setActiveGoodToKnowCarouselIdx,
};

export default connect(mapStateToProps, mapDispatchToProps)(AreaHighlights);
